import { ArrowLeft, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { BlogPost, BlogPostSummary, SiteSettings } from '@saas-bot/types';

import { fetchPost, fetchPosts } from '../lib/api';
import { Markdown } from './markdown';
import { Container, CtaButton, Eyebrow, SmartLink } from './ui';

function useMeta(title: string, description: string, noindex = false): void {
  useEffect(() => {
    document.title = title;
    let tag = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'description');
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', description);
    let robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', noindex ? 'noindex, nofollow' : 'index, follow');
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [title, description, noindex]);
}

function PostMeta({ post }: { post: BlogPostSummary }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm text-[var(--color-ink-muted)]">
      {post.authorName ? (
        <span className="font-medium text-[var(--color-ink)]">{post.authorName}</span>
      ) : null}
      {post.publishedAt ? (
        <time dateTime={post.publishedAt}>
          {new Date(post.publishedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
        </time>
      ) : null}
      <span className="inline-flex items-center gap-1">
        <Clock className="h-3.5 w-3.5" aria-hidden /> {post.readingMinutes} min read
      </span>
    </div>
  );
}

export function BlogList({ settings }: { settings: SiteSettings }): JSX.Element {
  const [posts, setPosts] = useState<BlogPostSummary[] | null>(null);
  useMeta(`Blog · ${settings.brandName}`, `Articles and updates from ${settings.brandName}.`);

  useEffect(() => {
    const controller = new AbortController();
    void fetchPosts(controller.signal).then(setPosts);
    return () => controller.abort();
  }, []);

  return (
    <section className="py-16 sm:py-24">
      <Container>
        <div className="flex flex-col items-start gap-4">
          <Eyebrow>Blog</Eyebrow>
          <h1 className="font-[var(--font-display)] text-4xl font-semibold leading-tight text-[var(--color-ink)] sm:text-5xl">
            Ideas on conversations that convert
          </h1>
        </div>

        {posts === null ? (
          <p className="mt-12 text-[var(--color-ink-muted)]">Loading…</p>
        ) : posts.length === 0 ? (
          <p className="mt-12 text-[var(--color-ink-muted)]">No posts yet. Check back soon.</p>
        ) : (
          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group flex flex-col overflow-hidden rounded-[var(--radius-2xl)] border border-[var(--color-line)] bg-[var(--color-surface)] transition-transform duration-200 hover:-translate-y-1"
              >
                {post.featuredImageUrl ? (
                  <img
                    src={post.featuredImageUrl}
                    alt=""
                    className="aspect-video w-full object-cover"
                  />
                ) : (
                  <div className="accent-bg aspect-video w-full opacity-10" aria-hidden />
                )}
                <div className="flex flex-1 flex-col gap-3 p-6">
                  {post.category ? <Eyebrow>{post.category}</Eyebrow> : null}
                  <h2 className="font-[var(--font-display)] text-xl font-semibold leading-snug text-[var(--color-ink)]">
                    {post.title}
                  </h2>
                  <p className="line-clamp-3 flex-1 text-[15px] leading-relaxed text-[var(--color-ink-soft)]">
                    {post.excerpt}
                  </p>
                  <PostMeta post={post} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </Container>
    </section>
  );
}

export function BlogPostView({ settings }: { settings: SiteSettings }): JSX.Element {
  const { postSlug } = useParams();
  const [state, setState] = useState<'loading' | 'ready' | 'missing'>('loading');
  const [post, setPost] = useState<BlogPost | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    void fetchPost(postSlug ?? '', controller.signal).then((result) => {
      setPost(result);
      setState(result ? 'ready' : 'missing');
    });
    return () => controller.abort();
  }, [postSlug]);

  useMeta(
    post
      ? (post.seoTitle ?? `${post.title} · ${settings.brandName}`)
      : `Blog · ${settings.brandName}`,
    post?.seoDescription ?? post?.excerpt ?? '',
    post?.noindex ?? false,
  );

  if (state === 'loading') {
    return (
      <section className="py-24">
        <Container>
          <p className="text-[var(--color-ink-muted)]">Loading…</p>
        </Container>
      </section>
    );
  }

  if (state === 'missing' || !post) {
    return (
      <section className="py-24">
        <Container className="flex flex-col items-start gap-4">
          <h1 className="font-[var(--font-display)] text-3xl font-semibold text-[var(--color-ink)]">
            Post not found
          </h1>
          <SmartLink
            href="/blog"
            className="accent-text inline-flex items-center gap-1.5 font-semibold"
          >
            <ArrowLeft className="h-4 w-4" /> Back to blog
          </SmartLink>
        </Container>
      </section>
    );
  }

  return (
    <article className="py-16 sm:py-24">
      <Container className="max-w-3xl">
        <SmartLink
          href="/blog"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
        >
          <ArrowLeft className="h-4 w-4" /> All posts
        </SmartLink>
        <div className="mt-6 flex flex-col gap-4">
          {post.category ? <Eyebrow>{post.category}</Eyebrow> : null}
          <h1 className="font-[var(--font-display)] text-4xl font-semibold leading-tight text-[var(--color-ink)] sm:text-5xl">
            {post.title}
          </h1>
          <PostMeta post={post} />
        </div>

        {post.featuredImageUrl ? (
          <img
            src={post.featuredImageUrl}
            alt=""
            className="mt-8 w-full rounded-[var(--radius-2xl)] border border-[var(--color-line)] object-cover"
          />
        ) : null}

        <div className="mt-8">
          <Markdown body={post.body} />
        </div>

        {post.tags.length > 0 ? (
          <div className="mt-10 flex flex-wrap gap-2 border-t border-[var(--color-line)] pt-6">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-[var(--color-cream-200)] px-3 py-1 text-xs font-medium text-[var(--color-ink-soft)]"
              >
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        <div className="mt-12 flex flex-wrap items-center gap-3 rounded-[var(--radius-2xl)] border border-[var(--color-line)] bg-[var(--color-surface)] p-6">
          <span className="flex-1 font-[var(--font-display)] text-lg font-semibold text-[var(--color-ink)]">
            See Vaanexa on your own conversations.
          </span>
          <CtaButton link={settings.primaryCta} variant="primary" withArrow />
        </div>
      </Container>
    </article>
  );
}
